/**
 * @fileoverview Modal to add a custom song when search returns no results.
 * @summary Two fields: title and artist. Submits to local cache then optionally to queue.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/AddCustomSongModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons'
import type { FC } from 'react'

/**
 * @summary Props for AddCustomSongModal component.
 */
interface AddCustomSongModalProps {
  /** Whether the modal is open. */
  isOpen: boolean
  /** Initial title (e.g. from search query). */
  initialTitle?: string
  /** Initial artist (e.g. from advanced search). */
  initialArtist?: string
  /** Callback when the user submits the form. */
  onSubmit: (title: string, artist: string) => void
  /** Callback when the user cancels or closes. */
  onClose: () => void
  /** Whether the submit is in progress. */
  isSubmitting?: boolean
}

/**
 * @summary Modal to add a custom song (title + artist) to the local cache.
 */
const AddCustomSongModal: FC<AddCustomSongModalProps> = ({
  isOpen,
  initialTitle = '',
  initialArtist = '',
  onSubmit,
  onClose,
  isSubmitting = false,
}) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState(initialTitle)
  const [artist, setArtist] = useState(initialArtist)

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle)
      setArtist(initialArtist)
    }
  }, [isOpen, initialTitle, initialArtist])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const tTrim = title.trim()
    const aTrim = artist.trim()
    if (tTrim && aTrim) {
      onSubmit(tTrim, aTrim)
    }
  }

  if (!isOpen) return null

  const isValid = title.trim().length > 0 && artist.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" aria-labelledby="add-custom-song-title">
      <div className="bg-(--color-bg-primary) rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 id="add-custom-song-title" className="text-lg font-semibold text-(--color-text-primary) flex items-center gap-2">
            <FontAwesomeIcon icon={faPlus} />
            {t('singer.addCustomSong')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-(--color-text-secondary) hover:text-(--color-text-primary) rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <p className="text-sm text-(--color-text-secondary) mb-4">
          {t('singer.addCustomSongDescription')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="add-custom-title" className="block text-sm font-medium text-(--color-text-primary) mb-1">
              {t('singer.titlePlaceholder')}
            </label>
            <input
              id="add-custom-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('singer.titlePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              autoFocus
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="add-custom-artist" className="block text-sm font-medium text-(--color-text-primary) mb-1">
              {t('singer.artistPlaceholder')}
            </label>
            <input
              id="add-custom-artist"
              type="text"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              placeholder={t('singer.artistPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} />
                  {t('singer.addAndQueue')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddCustomSongModal
